/**
 * SECRET SHAPES — plan-019 DOM (ED-27, SEC-HA-8, AC-HA-15; design §31.14.2; ADR 0065, proposed; glossary §6.33
 * "Secret shape"). Contract IC-H3.
 *
 * `scrubSecretShape(text)` runs in the help host on every message before the `turn` row and the model, and in the api
 * on every help request's text (and an `offer` summary). Each match is replaced by {@link SECRET_SHAPE_PLACEHOLDER}:
 *
 * | Kind | Shape |
 * |---|---|
 * | `iban` | country letters + 2 check digits + 11–30 letters and digits (single spaces allowed), mod-97 valid, at least 8 digits after the check digits |
 * | `api_key` | the wetalk API key shape: `wtk_<word>_` and at least 16 base64url characters |
 * | `card` | a Luhn-valid run of 13–19 digits, single spaces or hyphens allowed between groups, not glued to a letter |
 * | `password` | after "password" / "passwd" / "passcode" / «κωδικός πρόσβασης» / «συνθηματικό», to the end of the line, when the words that follow disclose a value (below) |
 * | `one_time_code` | a 4–8 digit run (or `ddd ddd`; never a year 1900–2099) within six words of "code" / "passcode" / "otp" / "pin" / «κωδικός» / «κωδικό» … |
 * | `link_token` | in a wetalk URL (`wetalk.io` or a subdomain): a `#fragment` of 16+ base64url characters that is not a heading anchor (lower-case words joined by hyphens), or the value (6+ characters) of a `token`, `code`, `key`, `secret`, `signature`, `sig`, `otp`, `auth`, `reset`, `invite`, `invitation` or `verification` query parameter — a password-reset, sign-in, invitation or verification link. Only the token goes; the page stays readable |
 * | `long_secret` | a run of 32+ base64url or hex characters with a letter and a digit, within six words of "secret" / "token" / "digest" / "signing" / "password" / «κωδικ…» / «μυστικ…» — never a UUID (an id the console shows) |
 *
 * **No false positive on ordinary help text** (design §31.14.2's cases): a phone number (10–12 digits, not Luhn-valid at
 * card length, and never near "code"), a price, a date (`2026-10-15`, `15/10/2026` — a digit run joined to another by
 * `.`, `,`, `:`, `/` or `-` is part of a bigger number), an order of steps. Two narrowings keep questions about a
 * password or a code intact (a departure from the literal "text after password", recorded in plan-019 §7 by INT):
 *
 * - `password`: the rest of the line is removed only when a value is disclosed — after `:` or `=`, after "is" / "was" /
 *   «είναι» / «ήταν» unless the next word is an ordinary word ("not", "wrong", "the", «δεν», «λάθος» …), or when the next
 *   word itself looks like a secret (a digit or a symbol, 4 characters or more). "How do I change my password?" and
 *   "my password is not accepted" are kept.
 * - `one_time_code`: a qualified code ("postal code", "area code", "error code", «ταχυδρομικός κώδικας» …) is not a
 *   secret.
 *
 * Matching is case- and accent-insensitive (Greek written with or without tonos, either sigma). Pure; no I/O; a span
 * found by two rules is replaced once.
 */
/** What replaces a secret, in storage and in the model input. */
export const SECRET_SHAPE_PLACEHOLDER = "[removed]";
export const SECRET_SHAPE_KIND = ["iban", "api_key", "card", "password", "one_time_code", "link_token", "long_secret"];
/** Replaces every secret shape in `text` (see the module comment). */
export function scrubSecretShape(text) {
    const folded = foldOf(text);
    const found = [
        ...ibanSpansOf(text),
        ...apiKeySpansOf(text),
        ...cardSpansOf(text),
        ...passwordSpansOf(folded),
        ...codeSpansOf(text, folded),
        ...linkTokenSpansOf(text),
        ...longSecretSpansOf(text, folded),
    ];
    if (found.length === 0)
        return { text, scrubbed: false, kind: [] };
    const merged = mergedOf(found);
    let out = "";
    let at = 0;
    for (const span of merged) {
        out += text.slice(at, span.start) + SECRET_SHAPE_PLACEHOLDER;
        at = span.end;
    }
    out += text.slice(at);
    const kinds = new Set(found.map((span) => span.kind));
    return { text: out, scrubbed: true, kind: SECRET_SHAPE_KIND.filter((kind) => kinds.has(kind)) };
}
function mergedOf(spans) {
    const sorted = [...spans].sort((a, b) => a.start - b.start || b.end - a.end);
    const merged = [];
    for (const span of sorted) {
        const last = merged[merged.length - 1];
        if (last !== undefined && span.start <= last.end) {
            if (span.end > last.end)
                merged[merged.length - 1] = { start: last.start, end: span.end, kind: last.kind };
        }
        else {
            merged.push(span);
        }
    }
    return merged;
}
/**
 * Lower case without accents, one UTF-16 unit for one, so every index in the folded text is the same index in the
 * original. A character whose folding would change the length (a surrogate pair, "İ") is kept as it is.
 */
function foldOf(text) {
    let out = "";
    for (let i = 0; i < text.length; i += 1) {
        const unit = text.charAt(i);
        const folded = unit.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
        out += folded.length === 1 ? folded : unit;
    }
    return out;
}
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
function isLetterOrDigitAt(text, index) {
    return index >= 0 && index < text.length && LETTER_OR_DIGIT.test(text.charAt(index));
}
// ── IBAN ────────────────────────────────────────────────────────────────────────────────────
const IBAN_CANDIDATE = /(?<![\p{L}\p{N}])[A-Za-z]{2}\d{2}(?: ?[A-Za-z0-9]){11,30}/gu;
function ibanSpansOf(text) {
    const spans = [];
    for (const match of text.matchAll(IBAN_CANDIDATE)) {
        const start = match.index;
        // The compact characters and where each ends in the original.
        const compact = [];
        const endOf = [];
        for (let i = start; i < start + match[0].length; i += 1) {
            const char = text.charAt(i);
            if (char === " ")
                continue;
            compact.push(char.toUpperCase());
            endOf.push(i + 1);
        }
        for (let length = Math.min(34, compact.length); length >= 15; length -= 1) {
            const end = endOf[length - 1] ?? start;
            if (isLetterOrDigitAt(text, end))
                continue;
            const candidate = compact.slice(0, length).join("");
            if ((candidate.slice(4).match(/\d/g)?.length ?? 0) < 8)
                continue;
            if (ibanChecksumValid(candidate)) {
                spans.push({ start, end, kind: "iban" });
                break;
            }
        }
    }
    return spans;
}
/** ISO 13616 mod-97: the first four characters moved to the end, letters as 10–35, the remainder 1. */
function ibanChecksumValid(iban) {
    const check = Number(iban.slice(2, 4));
    if (check < 2 || check > 98)
        return false;
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    let remainder = 0;
    for (const char of rearranged) {
        const value = /\d/.test(char) ? char : String(char.charCodeAt(0) - 55);
        for (const digit of value)
            remainder = (remainder * 10 + Number(digit)) % 97;
    }
    return remainder === 1;
}
// ── the wetalk API key ──────────────────────────────────────────────────────────────────────
/** `wtk_live_` + 43 base64url characters as issued; a partial key of 16 or more is still a secret. */
const API_KEY = /(?<![A-Za-z0-9_])wtk_[a-z]{2,10}_[A-Za-z0-9_-]{16,}/g;
function apiKeySpansOf(text) {
    return [...text.matchAll(API_KEY)].map((match) => ({ start: match.index, end: match.index + match[0].length, kind: "api_key" }));
}
// ── card numbers ────────────────────────────────────────────────────────────────────────────
const DIGIT_RUN = /\d+(?:[ -]\d+)*/g;
function cardSpansOf(text) {
    const spans = [];
    for (const match of text.matchAll(DIGIT_RUN)) {
        // A run glued to a letter is an identifier's tail (an IBAN's "GR16 0110 …", an invoice "INV2026…"), not a card.
        if (/\p{L}/u.test(text.charAt(match.index - 1)))
            continue;
        const groups = [];
        for (const group of match[0].matchAll(/\d+/g)) {
            groups.push({ start: match.index + group.index, end: match.index + group.index + group[0].length, digits: group[0] });
        }
        const candidates = [];
        for (let i = 0; i < groups.length; i += 1) {
            let digits = "";
            for (let j = i; j < groups.length; j += 1) {
                digits += groups[j]?.digits ?? "";
                if (digits.length > 19)
                    break;
                if (digits.length >= 13 && luhnValid(digits)) {
                    candidates.push({ start: groups[i]?.start ?? 0, end: groups[j]?.end ?? 0, kind: "card" });
                }
            }
        }
        // The longest first, then whatever does not overlap it.
        candidates.sort((a, b) => b.end - b.start - (a.end - a.start));
        for (const candidate of candidates) {
            if (spans.every((taken) => candidate.end <= taken.start || candidate.start >= taken.end))
                spans.push(candidate);
        }
    }
    return spans;
}
function luhnValid(digits) {
    let sum = 0;
    for (let i = 0; i < digits.length; i += 1) {
        let value = Number(digits.charAt(digits.length - 1 - i));
        if (i % 2 === 1) {
            value *= 2;
            if (value > 9)
                value -= 9;
        }
        sum += value;
    }
    return sum % 10 === 0;
}
// ── passwords (on the folded text) ──────────────────────────────────────────────────────────
const PASSWORD_WORD = "(?:password|passwd|passcode|κωδικο[σς]\\s+προσβαση[σς]?|συνθηματικο)";
/** The keyword, an optional possessive, then `:` / `=` or a disclosing verb; the value starts at group 1's end. */
const PASSWORD_DISCLOSED = new RegExp(`(?<![\\p{L}\\p{N}])${PASSWORD_WORD}(?:\\s+(?:μου|mine))?(\\s*[:=]\\s*|\\s+(?:is|was|ειναι|ηταν)(?![\\p{L}\\p{N}])\\s*[:=]?\\s*)(?=\\S)`, "gu");
/** The keyword followed directly by a word that looks like a secret: a digit or a symbol in it, 4 characters or more. */
const PASSWORD_BARE = new RegExp(`(?<![\\p{L}\\p{N}])(${PASSWORD_WORD}\\s+)(?=[^\\s]*[\\p{N}!@#$%^&*()_+=\\[\\]{};'"\\\\|<>/?~\`])[^\\s]{4,}`, "gu");
/** Words that start an ordinary sentence about a password, never a value. */
const ORDINARY_NEXT_WORD = new Set([
    "not", "no", "wrong", "incorrect", "invalid", "expired", "correct", "right", "too", "still", "the", "a", "an", "my",
    "being", "now", "already", "same", "different", "missing", "blocked", "locked", "forgotten", "lost", "reset", "changed",
    "δεν", "λαθος", "λανθασμενος", "σωστος", "ο", "η", "το", "ιδιος", "ακομα", "ακομη", "παλιος", "νεος",
]);
function passwordSpansOf(folded) {
    const spans = [];
    const lineEndOf = (from) => {
        const end = folded.slice(from).search(/[\r\n]/);
        return end === -1 ? folded.length : from + end;
    };
    for (const match of folded.matchAll(PASSWORD_DISCLOSED)) {
        const valueStart = match.index + match[0].length;
        const end = lineEndOf(valueStart);
        // A value has a letter or a digit ("what is my password?" discloses nothing).
        if (!/[\p{L}\p{N}]/u.test(folded.slice(valueStart, end)))
            continue;
        const nextWord = /^[\p{L}\p{N}]+/u.exec(folded.slice(valueStart))?.[0] ?? "";
        const disclosedByVerb = !/[:=]/.test(match[1] ?? "");
        if (disclosedByVerb && ORDINARY_NEXT_WORD.has(nextWord))
            continue;
        spans.push({ start: valueStart, end, kind: "password" });
    }
    for (const match of folded.matchAll(PASSWORD_BARE)) {
        const valueStart = match.index + (match[1]?.length ?? 0);
        spans.push({ start: valueStart, end: lineEndOf(valueStart), kind: "password" });
    }
    return spans;
}
// ── one-time codes ──────────────────────────────────────────────────────────────────────────
/** A code keyword on the folded text: English words, and every Greek form of «κωδικός». */
const CODE_WORD = /(?<![\p{L}\p{N}])(?:code|passcode|otp|pin|κωδικ\p{L}*)(?![\p{L}\p{N}])/gu;
/** A word right before the keyword that makes it an ordinary code, not a secret. */
const CODE_QUALIFIER = /(?<![\p{L}\p{N}])(?:postal|post|zip|area|country|dialling|dialing|promo|promotional|discount|coupon|voucher|error|status|http|source|qr|bar|ταχυδρομικ\p{L}*|περιοχ\p{L}*|χωρα\p{L}*|σφαλματο\p{L}*|εκπτωσ\p{L}*|κουπον\p{L}*)$/u;
/** A 4–8 digit run, or `ddd ddd`, that is not part of a bigger number (a date, a decimal, a phone with separators). */
const CODE_DIGITS = /(?<![\p{L}\p{N}]|\d[.,:/-])(?:\d{4,8}|\d{3} \d{3})(?![\p{L}\p{N}]|[.,:/-]\d)/gu;
/** "within six words" (design §31.14.2). */
const CODE_WORD_DISTANCE = 6;
/** The index of the whitespace-separated word an offset of `text` falls in. */
function wordIndexerOf(text) {
    const wordStarts = [...text.matchAll(/\S+/g)].map((match) => match.index);
    return (at) => {
        let index = 0;
        while (index + 1 < wordStarts.length && (wordStarts[index + 1] ?? Infinity) <= at)
            index += 1;
        return index;
    };
}
function codeSpansOf(text, folded) {
    const wordIndexOf = wordIndexerOf(text);
    const keywordWord = [];
    for (const match of folded.matchAll(CODE_WORD)) {
        const before = folded.slice(0, match.index).trimEnd();
        if (CODE_QUALIFIER.test(before))
            continue;
        keywordWord.push(wordIndexOf(match.index));
    }
    if (keywordWord.length === 0)
        return [];
    const spans = [];
    for (const match of text.matchAll(CODE_DIGITS)) {
        // A year (1900–2099) is a date, not a code.
        if (/^(?:19|20)\d{2}$/.test(match[0]))
            continue;
        const word = wordIndexOf(match.index);
        if (keywordWord.some((keyword) => Math.abs(keyword - word) <= CODE_WORD_DISTANCE)) {
            spans.push({ start: match.index, end: match.index + match[0].length, kind: "one_time_code" });
        }
    }
    return spans;
}
// ── a wetalk link carrying a token (plan-019 REVIEW-FIX) ────────────────────────────────────
/** A wetalk URL: scheme, `wetalk.io` or a subdomain, then the path, the query and the fragment up to a space or a quote. */
const WETALK_URL = /(?<![\p{L}\p{N}])https?:\/\/(?:[a-z0-9-]+\.)*wetalk\.io(?::\d{1,5})?(?=[/?#\s]|$)[^\s<>"'`]*/giu;
/** Query parameters whose value is a credential in a reset, sign-in, invitation or verification link. */
const TOKEN_PARAMETER = new Set(["token", "code", "key", "secret", "signature", "sig", "otp", "auth", "reset", "invite", "invitation", "verification"]);
/** A heading anchor (`#connect-your-sip-trunk`): lower-case words joined by hyphens — never a token. */
const HEADING_ANCHOR = /^[a-z]+(?:-[a-z0-9]+)*$/;
/** Punctuation that ends a sentence, not a URL. */
const URL_TRAILING = /[.,;:!?)\]}»"']+$/;
function linkTokenSpansOf(text) {
    const spans = [];
    for (const match of text.matchAll(WETALK_URL)) {
        const url = match[0].replace(URL_TRAILING, "");
        const start = match.index;
        const hash = url.indexOf("#");
        const query = url.indexOf("?");
        if (hash !== -1) {
            const fragment = url.slice(hash + 1);
            if (/^[A-Za-z0-9_-]{16,}$/.test(fragment) && !HEADING_ANCHOR.test(fragment)) {
                spans.push({ start: start + hash + 1, end: start + url.length, kind: "link_token" });
            }
        }
        if (query !== -1 && (hash === -1 || query < hash)) {
            const queryEnd = hash === -1 ? url.length : hash;
            let at = query + 1;
            for (const pair of url.slice(query + 1, queryEnd).split("&")) {
                const equals = pair.indexOf("=");
                const name = equals === -1 ? "" : pair.slice(0, equals).toLowerCase();
                const value = equals === -1 ? "" : pair.slice(equals + 1);
                if (TOKEN_PARAMETER.has(name) && /^[A-Za-z0-9_.~%-]{6,}$/.test(value)) {
                    spans.push({ start: start + at + equals + 1, end: start + at + pair.length, kind: "link_token" });
                }
                at += pair.length + 1;
            }
        }
    }
    return spans;
}
// ── an unprefixed secret near a secret word (plan-019 REVIEW-FIX) ───────────────────────────
/** A secret word on the folded text: English, and every Greek form of «κωδικός» and «μυστικό». */
const SECRET_WORD = /(?<![\p{L}\p{N}])(?:secret\p{L}*|token\p{L}*|digest\p{L}*|signing|password\p{L}*|passwd|κωδικ\p{L}*|μυστικ\p{L}*)(?![\p{L}\p{N}])/gu;
/** A run of 32 or more base64url / hex characters, standing alone. */
const LONG_RUN = /(?<![A-Za-z0-9_-])[A-Za-z0-9_-]{32,}(?![A-Za-z0-9_-])/g;
/** An id the console shows (a UUID), never a secret. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** "within six words", as for codes. */
const SECRET_WORD_DISTANCE = 6;
function longSecretSpansOf(text, folded) {
    const wordIndexOf = wordIndexerOf(text);
    // A secret word inside an address ("…/reset-password#…") names a page, not a value: the link rule has that.
    const address = [...text.matchAll(/https?:\/\/\S+/gi)].map((match) => ({ start: match.index, end: match.index + match[0].length }));
    const keywordWord = [...folded.matchAll(SECRET_WORD)]
        .filter((match) => address.every((one) => match.index < one.start || match.index >= one.end))
        .map((match) => wordIndexOf(match.index));
    if (keywordWord.length === 0)
        return [];
    const spans = [];
    for (const match of text.matchAll(LONG_RUN)) {
        const run = match[0];
        // An ordinary long word has no digit; a UUID is an id; `wtk_…` is the api_key rule's.
        if (!/\d/.test(run) || !/[A-Za-z]/.test(run) || UUID.test(run))
            continue;
        const word = wordIndexOf(match.index);
        if (keywordWord.some((keyword) => Math.abs(keyword - word) <= SECRET_WORD_DISTANCE)) {
            spans.push({ start: match.index, end: match.index + run.length, kind: "long_secret" });
        }
    }
    return spans;
}
//# sourceMappingURL=scrub.js.map